import { useState } from 'preact/hooks';
import { get, passkeysSupported, post, registerPasskey, signInWithPasskey } from '../api';
import { refreshMe } from '../session';
import { Button, Card, Field, Modal, navigate, toast, useAction, useLoad } from '../ui';
import { GameCard } from './Browse';

export function Landing(p: { next?: string }) {
	const featured = useLoad(() => get('/api/games/featured'));
	const [creating, setCreating] = useState(false);
	const [recovering, setRecovering] = useState(false);
	const done = async () => {
		await refreshMe();
		navigate(p.next && p.next !== '/' ? p.next : '/home', true);
	};
	const [signIn, signingIn] = useAction(async () => {
		if (!passkeysSupported()) throw new Error('This browser does not support passkeys.');
		await signInWithPasskey();
		await done();
	});
	return (
		<main class="page landing">
			<section class="hero">
				<div class="hero-text">
					<h1>Stories that bend to what you do.</h1>
					<p class="lede">
						Shifting Saga is a multiplayer adventure run by a game master and a deterministic story engine. Up to eight players act together in live beats; language models
						interpret, adjudicate and narrate — but the engine owns the truth, so the world never drifts and nobody can talk their way into the dragon sword.
					</p>
					<div class="hero-actions">
						<Button onClick={() => signIn()} busy={signingIn}>
							Sign in with a passkey
						</Button>
						<Button variant="ghost" onClick={() => setCreating(true)}>
							Create account
						</Button>
					</div>
					<p class="small muted">
						No passwords anywhere. Lost every passkey?{' '}
						<button class="linkish" onClick={() => setRecovering(true)}>
							Use a recovery code
						</button>
						.
					</p>
				</div>
				<ul class="hero-points">
					<li>
						<strong>Write a story in prose.</strong> The compiler turns it into a playable world with areas, arcs, pivots and endings — and proves every path ends.
					</li>
					<li>
						<strong>Play in beats.</strong> Everyone in a scene acts at once under a shared timer; simultaneous actions resolve together.
					</li>
					<li>
						<strong>No puppeteering.</strong> Anything that would move or speak for another player becomes a question they answer.
					</li>
					<li>
						<strong>Bring your own model.</strong> Your API key, a local model on your own machine, or included models on paid plans.
					</li>
				</ul>
			</section>
			<Card title="Live and featured games">
				{featured.data?.games?.length ? (
					<div class="grid-cards">
						{featured.data.games.map((g: any) => (
							<GameCard key={g.code} g={g} />
						))}
					</div>
				) : (
					<p class="muted">No public games right now. Be the first to start one.</p>
				)}
			</Card>
			{creating ? <CreateAccount onClose={() => setCreating(false)} onDone={done} /> : null}
			{recovering ? <Recover onClose={() => setRecovering(false)} onDone={done} /> : null}
		</main>
	);
}

function CreateAccount(p: { onClose: () => void; onDone: () => Promise<void> }) {
	const [name, setName] = useState('');
	const [run, busy] = useAction(async () => {
		if (!passkeysSupported()) throw new Error('This browser does not support passkeys.');
		await registerPasskey(name.trim() || undefined);
		toast('Welcome! Your passkey is your account.', 'ok');
		await p.onDone();
	});
	return (
		<Modal title="Create account" onClose={p.onClose}>
			<p>Your device will create a passkey for this site. That passkey is your account — you can add more devices later.</p>
			<Field label="Display name (optional)" hint="Leave blank and we'll make one up. You can change it any time.">
				<input value={name} onInput={(e) => setName(e.currentTarget.value)} maxLength={40} placeholder="e.g. Brave Otter 42" />
			</Field>
			<div class="row-end">
				<Button onClick={() => run()} busy={busy}>
					Create passkey
				</Button>
			</div>
		</Modal>
	);
}

function Recover(p: { onClose: () => void; onDone: () => Promise<void> }) {
	const [code, setCode] = useState('');
	const [run, busy] = useAction(async () => {
		await post('/api/auth/recover', { code });
		toast('Signed in. Add a new passkey from your account page now.', 'ok', 8000);
		await p.onDone();
		navigate('/account');
	});
	return (
		<Modal title="Recover your account" onClose={p.onClose}>
			<Field label="Recovery code" hint="Each code works once.">
				<input value={code} onInput={(e) => setCode(e.currentTarget.value)} placeholder="ABCD-EFGH-IJKL" />
			</Field>
			<div class="row-end">
				<Button onClick={() => run()} busy={busy} disabled={code.length < 8}>
					Sign in
				</Button>
			</div>
		</Modal>
	);
}
